import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import storage from '../utils/storage';
import { registerDeviceTokenWithBackend } from '../capacitor-utils';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

// Clear all cookies (for iOS WebView)
const clearAllCookies = () => {
  if (typeof document !== 'undefined') {
    const cookies = document.cookie.split(";");
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i];
      const eqPos = cookie.indexOf("=");
      const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
      document.cookie = name.trim() + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/";
      document.cookie = name.trim() + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=" + window.location.hostname;
    }
  }
};

// Setup axios interceptor to add token to all requests
const setupAxiosInterceptor = () => {
  axios.interceptors.request.use(
    async (config) => {
      try {
        const token = await storage.get('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        console.error('Error getting token for request:', error);
      }
      return config;
    },
    (error) => Promise.reject(error)
  );
};

// Call setup once
setupAxiosInterceptor();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  // Re-register device token whenever user becomes authenticated (handles app cold-start while logged in)
  useEffect(() => {
    if (user && Capacitor.isNativePlatform()) {
      registerDeviceTokenWithBackend().catch(() => {});
    }
  }, [user?.id]);

  const checkAuth = async () => {
    try {
      // On native platforms, wait longer for storage to be ready
      if (Capacitor.isNativePlatform()) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
      
      // First check if we have a token in storage
      let token = await storage.get('auth_token');
      
      // On native, retry multiple times if token not found (iOS can be slow)
      if (!token && Capacitor.isNativePlatform()) {
        for (let i = 0; i < 3; i++) {
          await new Promise(resolve => setTimeout(resolve, 300));
          token = await storage.get('auth_token');
          if (token) break;
        }
      }
      
      if (!token) {
        // No token found - check if we have cached user (offline scenario)
        const cachedUser = await storage.get('cached_user');
        if (cachedUser) {
          console.log('📱 No token but cached user found - might be storage issue');
        }
        setUser(null);
        setLoading(false);
        return;
      }
      
      // We have a token — v50.10.8 "native-like" start: hydrate instantly from cache
      // (works fully offline), then validate/refresh in the background.
      let hydratedFromCache = false;
      try {
        const cachedUser = await storage.get('cached_user');
        if (cachedUser) {
          const userData = typeof cachedUser === 'string' ? JSON.parse(cachedUser) : cachedUser;
          if (userData?.id) {
            setUser(userData);
            setLoading(false);
            hydratedFromCache = true;
          }
        }
      } catch (_e) { /* ignore cache parse errors */ }

      // We have a token - try to validate it (bounded: never block the UI on a slow network)
      try {
        const response = await axios.get('/api/auth/me', { timeout: hydratedFromCache ? 15000 : 8000 });
        setUser(response.data);
        
        // Auto-renew token if server provided a new one
        if (response.data.renewed_token) {
          console.log('🔄 Token auto-renewed by server');
          await storage.set('auth_token', response.data.renewed_token);
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.renewed_token}`;
        }
        
        // Update cached user on successful auth
        try {
          await storage.set('cached_user', JSON.stringify(response.data));
        } catch (e) {
          // Ignore cache errors
        }
      } catch (error) {
        // Check if it's a network error (offline) vs an auth error (401)
        const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || error.message === 'Network Error' || /timeout/i.test(error.message || '');
        const isAuthError = error.response?.status === 401;
        
        if (isNetworkError) {
          // Offline mode: Keep user logged in with cached data
          console.log('📡 Network error during auth check - enabling offline mode');
          const cachedUser = await storage.get('cached_user');
          if (cachedUser) {
            try {
              const userData = typeof cachedUser === 'string' ? JSON.parse(cachedUser) : cachedUser;
              setUser(userData);
              console.log('📱 Using cached user data for offline mode:', userData?.email);
            } catch (e) {
              console.log('⚠️ Could not parse cached user, but keeping token');
              // Even if we can't parse, don't log out - keep the session
            }
          } else {
            console.log('📱 No cached user, but keeping token for next online check');
            // Don't set user to null - keep loading state and retry later
          }
          // NEVER clear the token for network errors
        } else if (isAuthError) {
          // Token is invalid (401) - this is the ONLY case where we should clear
          console.log('🔐 Auth error 401 - token is invalid, clearing session');
          await storage.remove('auth_token');
          await storage.remove('cached_user');
          setUser(null);
        } else {
          // Other errors (500, timeout, etc.) - don't clear token, might be temporary
          console.log('⚠️ Temporary error during auth check:', error.message);
          // Try to use cached user
          const cachedUser = await storage.get('cached_user');
          if (cachedUser) {
            try {
              setUser(typeof cachedUser === 'string' ? JSON.parse(cachedUser) : cachedUser);
              console.log('📱 Using cached user due to server error');
            } catch (e) {
              // Keep token, don't log out
            }
          }
        }
      }
    } catch (error) {
      console.error('❌ Fatal error in checkAuth:', error);
      // Even on fatal errors, try to preserve the session
      try {
        const cachedUser = await storage.get('cached_user');
        if (cachedUser) {
          setUser(typeof cachedUser === 'string' ? JSON.parse(cachedUser) : cachedUser);
        }
      } catch (e) {
        // Ignore
      }
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      console.log('🔐 Login attempt to:', axios.defaults.baseURL || 'relative URL');
      console.log('🌐 Platform:', Capacitor.getPlatform());
      console.log('📱 Is native:', Capacitor.isNativePlatform());

      // New OTP-aware endpoint. Legacy accounts get a token back directly;
      // post-migration accounts get { two_factor: true, user_id, email_masked }.
      const response = await axios.post('/api/auth/login/start', { email, password });

      // 2FA required — bail out early. Caller must call loginVerify(user_id, code).
      if (response.data?.two_factor === true) {
        return response.data;
      }

      // Legacy single-step login (no 2FA)
      if (response.data.token) {
        await storage.set('auth_token', response.data.token);
        if (Capacitor.isNativePlatform()) {
          await new Promise(resolve => setTimeout(resolve, 500));
          const storedToken = await storage.get('auth_token');
          if (!storedToken) {
            await storage.set('auth_token', response.data.token);
            await new Promise(resolve => setTimeout(resolve, 300));
          }
        }
      }
      setUser(response.data.user);
      try {
        await storage.set('cached_user', JSON.stringify(response.data.user));
      } catch (e) {
        console.log('⚠️ Could not cache user data:', e.message);
      }
      if (Capacitor.isNativePlatform()) {
        try { await registerDeviceTokenWithBackend(); } catch (_e) { /* ignore */ }
      }
      return new Promise((resolve) => setTimeout(() => resolve(response.data), 100));
    } catch (error) {
      console.error('❌ Login error:', error.response?.status, error.response?.data);
      throw error;
    }
  };

  const loginVerify = async (userId, code) => {
    const response = await axios.post('/api/auth/login/verify', { user_id: userId, code });
    if (response.data.token) {
      await storage.set('auth_token', response.data.token);
    }
    setUser(response.data.user);
    try { await storage.set('cached_user', JSON.stringify(response.data.user)); } catch (_e) { /* ignore */ }
    if (Capacitor.isNativePlatform()) {
      try { await registerDeviceTokenWithBackend(); } catch (_e) { /* ignore */ }
    }
    return new Promise((resolve) => setTimeout(() => resolve(response.data), 100));
  };

  const register = async (email, password, name, account_type, siret, date_of_birth, accepts_terms) => {
    // Capture referrer from URL (`?ref=<username>`) — used for the free referral counter.
    let ref = null;
    try {
      const params = new URLSearchParams(window.location.search);
      ref = params.get('ref');
    } catch (_) { /* ignore */ }
    const response = await axios.post('/api/auth/register', {
      email,
      password,
      name,
      account_type,
      siret,
      date_of_birth,
      accepts_terms,
      ...(ref ? { ref } : {}),
    });
    // Store token for mobile compatibility
    if (response.data.token) {
      await storage.set('auth_token', response.data.token);
      // Small delay to ensure token is persisted on iOS
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    setUser(response.data.user);
    return response.data;
  };

  const logout = async () => {
    try {
      // First clear local state
      setUser(null);
      
      // Clear token from storage
      await storage.remove('auth_token');
      
      // Clear all cookies
      clearAllCookies();
      
      // Call backend to invalidate session
      try {
        await axios.post('/api/auth/logout');
      } catch (e) {
        // Ignore logout API errors
      }
      
      // Clear storage again to be sure
      await storage.remove('auth_token');
      await storage.clear();
      
      // Clear cookies again
      clearAllCookies();
      
      // Small delay to ensure everything is cleared
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Force reload to clear any cached state
      if (Capacitor.isNativePlatform()) {
        // On native, use replace to prevent back navigation
        window.location.replace('/');
      } else {
        window.location.href = '/';
      }
    } catch (error) {
      console.error('Logout error:', error);
      // Even on error, force redirect
      window.location.href = '/';
    }
  };

  // v50.10.8c — adopt a freshly created session (signup + email code verified) without a second login
  const adoptSession = async (token, userData) => {
    if (token) {
      await storage.set('auth_token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    let u = userData;
    try {
      const me = await axios.get('/api/auth/me');
      u = me.data || userData;
    } catch (_e) { /* keep the register payload */ }
    if (u) {
      setUser(u);
      try { await storage.set('cached_user', JSON.stringify(u)); } catch (_e) { /* ignore */ }
    }
    if (Capacitor.isNativePlatform()) {
      try { await registerDeviceTokenWithBackend(); } catch (_e) { /* ignore */ }
    }
    return u;
  };

  const value = {
    user,
    loading,
    login,
    loginVerify,
    register,
    adoptSession,
    logout,
    refreshUser: checkAuth
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
